import { Box, Grid, Paper, Typography, Skeleton } from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import React, { useEffect, useState } from "react";
import { parseISO, startOfWeek, endOfWeek, format, startOfDay, startOfHour } from "date-fns";
import CustomTypography from "../../../core/components/ui/CustomTypography";
import useDashboard from "../hooks/useDashboard";
import useUserProfile from "../../ProfilePlugin/hooks/useUserProfile";
import { useGuard, RBACUIGuard } from "../../RBACPlugin";
import EmptyBox from "../../../assets/icons/empty-box.png";
import { permissions } from "../../../core/components/common/variables/static_variables";
import moment from "moment";
import DateRangePickerComponent from "../../../core/components/common/custom-date-rage.component";
import GatepassAnalyticsComponent from "../components/gatepass-analytics.component";
import BookingAnalyticsComponent from "../components/booking-analytics.component";
import AccessLogsAnalyticsComponent from "../components/accesss-logs-analytics.component";
import VehicleAnalyticsComponent from "../components/vehicle-analytics.component";
import ContainerAnalyticsComponent from "../components/container-analytics.component";
import { get } from "lodash";
import { useNavigate } from "react-router-dom";

export default function DashboardPage() {
  const rbacStorage = JSON.parse(localStorage.getItem("rbac-storage"));
  const org_uid = rbacStorage?.state?.currentProfile?.orgId || null;

  const {
    countAccessLogs, accessLogs, isAccessLogsLoading,
    countGatepass, gatepassCount, isGatepassLoading,
    countVehicle, vehicleCount, isVehicleLoading,
    countBooking, bookingCount, isBookingLoading,
    countContainer, containerCount, isContainerLoading,
    getUserCount, userCount,
    bookingAnalytics,bookingGraph,
    gatepassAnalytics,gatepassGraph,
    containerAnalytics,containerGraph,
    vehicleAnalytics,vehicleGraph,
    getOrgNameByOrgUID, orgName
  } = useDashboard();

  const { viewProfile } = useUserProfile();
  const { hasPermission } = useGuard();
  const navigate = useNavigate()
  
  const [filteredGraphData, setFilteredGraphData] = useState([]);

  const [startDate, setStartDate] = useState(moment().subtract(6, "days"));
  const [endDate, setEndDate] = useState(moment());

  const [bookingStartDate, setBookingStartDate] = useState(moment().subtract(6, "days"));
  const [bookingEndDate, setBookingEndDate] = useState(moment())

  const [gatepassStartDate, setGatepassStartDate] = useState(moment().subtract(6, "days"));
  const [gatepassEndDate, setGatepassEndDate] = useState(moment());

  const [vehicleStartDate, setVehicleStartDate] = useState(moment().subtract(6, "days"));
  const [vehicleEndDate, setVehicleEndDate] = useState(moment())

  const [containerStartDate, setContainerStartDate] = useState(moment().subtract(6, "days"));
  const [containerEndDate, setContainerEndDate] = useState(moment());
  

  const handleBookingDateRangeFilter = (newStartDate, newEndDate) => {
    setBookingStartDate(moment.isMoment(newStartDate) ? newStartDate : moment(newStartDate));
    setBookingEndDate(moment.isMoment(newEndDate) ? newEndDate : moment(newEndDate));
  };

  const handleGatepassDateRangeFilter = (newStartDate, newEndDate) => {
    setGatepassStartDate(moment.isMoment(newStartDate) ? newStartDate : moment(newStartDate));
    setGatepassEndDate(moment.isMoment(newEndDate) ? newEndDate : moment(newEndDate));
  };

  const handleVehicleDateRangeFilter = (newStartDate, newEndDate) => {
    setVehicleStartDate(moment.isMoment(newStartDate) ? newStartDate : moment(newStartDate));
    setVehicleEndDate(moment.isMoment(newEndDate) ? newEndDate : moment(newEndDate));
  };

  const handleContainerDateRangeFilter = (newStartDate, newEndDate) => {
    setContainerStartDate(moment.isMoment(newStartDate) ? newStartDate : moment(newStartDate));
    setContainerEndDate(moment.isMoment(newEndDate) ? newEndDate : moment(newEndDate));
  };

  const handleDateRangeChange = (newStartDate, newEndDate) => {
    setStartDate(moment.isMoment(newStartDate) ? newStartDate : moment(newStartDate));
    setEndDate(moment.isMoment(newEndDate) ? newEndDate : moment(newEndDate));
  };

  useEffect(() => { viewProfile(); }, []);

  useEffect(() => {
    const params = { org_uid };
    countGatepass(params);
    countVehicle(params);
    countBooking(params);
    countContainer(params);
    getUserCount(params);
  }, [org_uid]);


  useEffect(() => {
    const accessLogsParams = {
      org_uid,
      start_date: startDate.format("YYYY-MM-DD"),
      end_date: endDate.format("YYYY-MM-DD")
    };
    countAccessLogs(accessLogsParams);
  }, [org_uid, startDate, endDate]);

  useEffect(() => {
    const params = {
      org_uid,
      start_date: bookingStartDate.format("YYYY-MM-DD"),
      end_date: bookingEndDate.format("YYYY-MM-DD")
    };
    bookingAnalytics(params);
  }, [org_uid, bookingStartDate, bookingEndDate]);

  useEffect(() => {
    const params = {
      org_uid,
      start_date: gatepassStartDate.format("YYYY-MM-DD"),
      end_date: gatepassEndDate.format("YYYY-MM-DD")
    };
    gatepassAnalytics(params);
  }, [org_uid, gatepassStartDate, gatepassEndDate]);

  useEffect(() => {
    const params = {
      org_uid,
      start_date: vehicleStartDate.format("YYYY-MM-DD"),
      end_date: vehicleEndDate.format("YYYY-MM-DD")
    };
    vehicleAnalytics(params);
  }, [org_uid, vehicleStartDate, vehicleEndDate]);

  useEffect(() => {
    const params = {
      org_uid,
      start_date: containerStartDate.format("YYYY-MM-DD"),
      end_date: containerEndDate.format("YYYY-MM-DD")
    };
    containerAnalytics(params);
  }, [org_uid, containerStartDate, containerEndDate]);



  const cardSources = [
    { title: "Bookings", count: bookingCount, requiredPermission: permissions.dashboard.DASHBOARD_BOOKING },
    { title: "Vehicles", count: vehicleCount, requiredPermission: permissions.dashboard.DASHBOARD_VEHICLE },
    { title: "Gatepasses", count: gatepassCount, requiredPermission: permissions.dashboard.DASHBOARD_GATEPASS },
    { title: "Container", count: containerCount, requiredPermission: permissions.dashboard.DASHBOARD_CONTAINER },
    { title: "Users", count: userCount, requiredPermission: permissions.dashboard.DASHBOARD_USERS }
  ].filter((item) => hasPermission(item.requiredPermission));

  const generateCards = ({ title, count }) => [{ title, count }];

  const cardData = cardSources.flatMap(generateCards);

  const getSizeProp = (count) => {
    if (count <= 1) return { xs: 12 };
    if (count === 2) return { xs: 12, sm: 6 };
    if (count === 3) return { xs: 12, sm: 6, md: 4 };
    if (count === 4) return { xs: 12, sm: 6, md: 3 };
    if (count === 5) return { xs: 12, sm: 6, md: 2.4 };
    return { xs: 12, sm: 6, md: 2.4 };
  };

  const size = getSizeProp(cardData.length);

  // Updated formatLabel to clip week/month start label to startDate
  

  const loadingCards = isBookingLoading || isVehicleLoading || isGatepassLoading ;

  const cardRoutes = {
    Bookings: "/bookings",
    Vehicles: "/vehicles",
    Gatepasses: "/gatepass",
    Container: "/containers", // plural for consistency
    Users: "/user"
  };

  return (
    <Box>
      <CustomTypography variant="h3" text="Dashboard" customStyles={{ mb: 0 }} />
      <CustomTypography
        variant="subtitle1"
        text="Offers a real-time overview of vehicle traffic and gatepass activity for efficient monitoring and management."
        customStyles={{ mb: 2 }}
      />

      <Grid container spacing={2}>
        {loadingCards
          ? Array.from({ length: 5 }).map((_, i) => (
            <Grid item key={i} size={{ xs: 12, sm: 2.4 }}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  textAlign: "center",
                  backgroundColor: "#f5f5f5"
                }}
              >
                <Skeleton variant="text" width="60%" height={30} sx={{ mx: "auto", mb: 1 }} />
                <Skeleton variant="text" width={60} height={40} sx={{ mx: "auto" }} />
              </Paper>
            </Grid>
          ))
          :
          <>
            {cardData.map((card, i) => (
              <Grid item key={i} size={size}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    textAlign: "center",
                    backgroundColor: "#f5f5f5",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                      if (cardRoutes[card.title]) {
                        navigate(cardRoutes[card.title]);
                      }
                  }}                
                >
                  <Typography variant="h6" gutterBottom>{card.title}</Typography>
                  {card.count != null ? (
                    <Typography variant="h4" fontWeight="bold" color="primary">
                      {card.count}
                    </Typography>
                  ) : (
                    <Skeleton variant="text" width={60} height={40} />
                  )}
                </Paper>
              </Grid>
            ))}
          </>
        }

         {/* ANALYTICS*/}
         <Grid item size={{xs: 6}}>
          <RBACUIGuard requiredPermission={permissions.dashboard.DASHBOARD_BOOKING_COMPONENT}>
            <BookingAnalyticsComponent  startDate={bookingStartDate} endDate={bookingEndDate} handleDateRangeChange={handleBookingDateRangeFilter} data={bookingGraph} orgName={orgName} getOrgNameByOrgUID={getOrgNameByOrgUID}/>
          </RBACUIGuard>
        </Grid>

        <Grid item size={{xs: 6}}>
          <RBACUIGuard requiredPermission={permissions.dashboard.DASHBOARD_GATEPASS_COMPONENT}>
            <GatepassAnalyticsComponent  startDate={gatepassStartDate} endDate={gatepassEndDate} handleDateRangeChange={handleGatepassDateRangeFilter} data={gatepassGraph} orgName={orgName} getOrgNameByOrgUID={getOrgNameByOrgUID}/>
          </RBACUIGuard>
        </Grid>

        <Grid item size={{xs: 6}}>
          <RBACUIGuard requiredPermission={permissions.dashboard.DASHBOARD_VEHICLE_COMPONENT}>
            <VehicleAnalyticsComponent  startDate={vehicleStartDate} endDate={vehicleEndDate} handleDateRangeChange={handleVehicleDateRangeFilter} data={vehicleGraph} orgName={orgName} getOrgNameByOrgUID={getOrgNameByOrgUID}/>
          </RBACUIGuard>
        </Grid>

        <Grid item size={{xs: 6}}>
          <RBACUIGuard requiredPermission={permissions.dashboard.DASHBOARD_CONTAINER_COMPONENT}>
            <ContainerAnalyticsComponent  startDate={containerStartDate} endDate={containerEndDate} handleDateRangeChange={handleContainerDateRangeFilter} data={containerGraph} orgName={orgName} getOrgNameByOrgUID={getOrgNameByOrgUID}/>
          </RBACUIGuard>
        </Grid>

        {/* ACCESSLOGS ANALYTICS WIDGET*/}
        <RBACUIGuard requiredPermission={permissions.dashboard.DASHBOARD_ACCESSLOGS}>
          <Grid item size={{ xs: 12 }}>
            <AccessLogsAnalyticsComponent startDate={startDate} endDate={endDate} handleDateRangeChange={handleDateRangeChange} accessLogs={accessLogs}/>
          </Grid>
        </RBACUIGuard>
        
      </Grid>

     
    </Box>
  );
}
